(() => {
  if (customElements.get('klaim-home-campaign')) return;
  const loadSwiper = src => {
    if (window.Swiper) return Promise.resolve();
    if (!window.klaimHeroSwiperReady) window.klaimHeroSwiperReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = () => { window.klaimHeroSwiperReady = null; reject(new Error('Campaign slider unavailable')); };
      document.head.append(script);
    });
    return window.klaimHeroSwiperReady;
  };
  class Campaign extends HTMLElement {
    async connectedCallback() {
      this.abort = new AbortController();
      const {signal} = this.abort;
      const listen = (node, event, handler) => node.addEventListener(event, handler, {signal});
      this.slides = [...this.querySelectorAll('.khc__slide')];
      this.steps = [...this.querySelectorAll('.khc__step')];
      this.delay = Number(this.dataset.delay) || 5000;
      this.paused = this.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches || Boolean(window.Shopify?.designMode);
      this.visible = true;
      this.index = 0;
      this.copyAnimations = [];
      this.copyIndex = -1;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.progressFill = this.querySelector('.khc__progress-fill');
      const size = () => {
        const header = document.querySelector('.header-group');
        this.style.setProperty('--khc-header', `${Math.round(header?.getBoundingClientRect().height || 96)}px`);
      };
      this.resize = new ResizeObserver(size);
      const header = document.querySelector('.header-group');
      if (header) this.resize.observe(header);
      size();
      listen(window, 'klaim:site-intro-complete', () => this.animateCopy(1));
      listen(this.reducedMotion, 'change', () => {
        this.copyAnimations.forEach(animation => animation.cancel());
        if (this.slider) this.slider.params.speed = this.reducedMotion.matches ? 0 : 850;
        if (this.reducedMotion.matches) { this.paused = true; this.update(); }
      });
      if (this.slides.length < 2) return;
      try { await loadSwiper(this.dataset.swiperSrc); } catch { return; }
      if (signal.aborted || !this.isConnected) return;
      // Native lazy loading can defer distant slides even during a direct jump.
      this.slides.slice(1).forEach(slide => {
        const image = slide.querySelector('img');
        if (image) { image.fetchPriority = 'low'; image.loading = 'eager'; }
      });
      this.slider = new window.Swiper(this.querySelector('.khc__viewport'), {
        slidesPerView: 1, speed: this.reducedMotion.matches ? 0 : 850, rewind: true, watchOverflow: true,
        a11y: {enabled: true},
        on: {
          touchStart: () => {this.dragging = true; this.syncPlayback();},
          touchEnd: () => {this.dragging = false; this.syncPlayback();},
          slideChange: swiper => {this.index = swiper.activeIndex; this.startProgress(); this.update();},
          slideChangeTransitionStart: swiper => {
            const direction = (swiper.activeIndex > swiper.previousIndex ? 1 : -1) * (swiper.rtlTranslate ? -1 : 1);
            this.animateCopy(direction);
          }
        }
      });
      this.querySelector('.khc__controls').hidden = false;
      this.toggle = this.querySelector('.khc__toggle');
      listen(this.querySelector('.khc__previous'), 'click', () => this.slider.slidePrev());
      listen(this.querySelector('.khc__next'), 'click', () => this.slider.slideNext());
      listen(this.toggle, 'click', () => {this.paused = !this.paused; this.update();});
      this.steps.forEach((step, index) => listen(step, 'click', () => this.slider.slideTo(index)));
      listen(this, 'keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.slider[event.key === 'ArrowRight' ? 'slideNext' : 'slidePrev']();
        }
      });
      // Pause for keyboard navigation without treating mouse or touch clicks as keyboard focus.
      listen(this, 'focusin', event => {
        if (event.target !== this.toggle && event.target.matches?.(':focus-visible')) {
          this.paused = true;
          this.update();
        }
      });
      listen(document, 'visibilitychange', () => {
        this.syncPlayback();
        if (document.hidden) this.copyAnimations.forEach(animation => animation.cancel());
        else this.animateCopy(1);
      });
      listen(window, 'klaim:site-intro-complete', () => this.syncPlayback());
      listen(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.paused = true;
        this.slider.slideTo(index, 0);
        this.update();
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncPlayback();
        if (this.visible) this.animateCopy(1);
        else this.copyAnimations.forEach(animation => animation.cancel());
      }, {threshold:.25});
      this.observer.observe(this);
      this.startProgress();
      this.update();
      this.animateCopy(1);
    }
    animateCopy(direction) {
      if (!this.visible || document.hidden || window.__klaimSiteIntroState === 'pending' || this.copyIndex === this.index) return;
      this.copyAnimations.forEach(animation => animation.cancel());
      this.copyAnimations = [];
      this.copyIndex = this.index;
      if (this.reducedMotion.matches || !Element.prototype.animate) return;
      // Animate the copy's contents, never the positioned meta wrapper or its dimensions.
      this.slides[this.index].querySelectorAll('.khc__copy, .khc__cta').forEach((element, index) => {
        this.copyAnimations.push(element.animate([
          { transform: `translateX(${direction * (index ? 112 : 80)}px)`, opacity: 0 },
          { transform: 'translateX(0)', opacity: 1 }
        ], { duration: 850, delay: index ? 220 : 120, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' }));
      });
    }
    startProgress() {
      if (!this.progressFill) return;
      this.progressAnimation?.cancel();
      const count = this.slides.length;
      this.progressAnimation = this.progressFill.animate([
        {transform:`scaleX(${this.index / count})`},
        {transform:`scaleX(${(this.index + 1) / count})`}
      ], {duration:this.delay, easing:'linear', fill:'forwards'});
      this.progressAnimation.onfinish = () => {
        if (this.slider && !this.slider.destroyed) this.slider.slideNext();
      };
      this.syncPlayback();
    }
    syncPlayback() {
      if (!this.progressAnimation) return;
      const playing = !this.paused && this.visible && !document.hidden && !this.dragging && window.__klaimSiteIntroState !== 'pending';
      if (playing && this.progressAnimation.playState === 'paused') this.progressAnimation.play();
      else if (!playing && this.progressAnimation.playState === 'running') this.progressAnimation.pause();
    }
    update() {
      this.slides.forEach((slide, index) => {slide.inert = index !== this.index; slide.setAttribute('aria-hidden', String(index !== this.index));});
      this.steps.forEach((step, index) => {if(index === this.index) step.setAttribute('aria-current','true'); else step.removeAttribute('aria-current');});
      this.style.setProperty('--khc-control-color', this.slides[this.index].dataset.tone);
      this.style.setProperty('--khc-control-color-mobile', this.slides[this.index].dataset.mobileTone);
      if (this.toggle) {
        this.toggle.dataset.paused = String(this.paused);
        const label = this.paused ? 'Play slideshow' : 'Pause slideshow';
        this.toggle.setAttribute('aria-label', label);
        this.toggle.title = label;
      }
      this.syncPlayback();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
      this.progressAnimation?.cancel();
      this.slider?.destroy(true, true);
      this.copyAnimations?.forEach(animation => animation.cancel());
    }
  }
  customElements.define('klaim-home-campaign', Campaign);
})();
